import { Maybe } from 'graphql/jsutils/Maybe'
import React from 'react'
import { FormattedMessage } from 'react-intl'

import BalancePaidFooter from './balance-paid-footer/BalancePaidFooter'
import BookingDeclinedByUserAdminFooter from './booking-declined-by-user-admin-footer/BookingDeclinedByUserAdminFooter'
import DepositPaidFooter from './deposit-paid-footer/DepositPaidFooter'
import PostBalancePaidFooter from './post-balance-paid-footer/PostBalancePaidFooter'
import UserCanAskQuotationFooter from './user-can-ask-quotation/user-can-ask-quotation-footer/UserCanAskQuotationFooter'
import UserCanAskUserAdminApprovalFooter from './user-can-ask-user-admin-approval/user-can-ask-user-admin-approval-footer/UserCanAskUserAdminApprovalFooter'
import UserCanConfirmAdjustmentsFooter from './user-can-confirm-adjustments/user-can-confirm-adjustments-footer/UserCanConfirmAdjustmentsFooter'
import UserCanConfirmBookingFooter from './user-can-confirm-booking/user-can-confirm-booking-footer/UserCanConfirmBookingFooter'
import UserCanPayBalanceFooter from './user-can-pay-balance-footer/UserCanPayBalanceFooter'
import UserCanPayDepositFooter from './user-can-pay-deposit-footer/UserCanPayDepositFooter'
import UserCanPayPostBalanceFooter from './user-can-pay-post-balance-footer/UserCanPayPostBalanceFooter'
import UserWaitingHostAdjustmentsConfirmationFooter from './user-waiting-host-adjustments-confirmation-footer/UserWaitingHostAdjustmentsConfirmationFooter'
import UserWaitingHostBookingConfirmationFooter from './user-waiting-host-booking-confirmation-footer/UserWaitingHostBookingConfirmationFooter'
import PingPongButton from '../buttons-by-step/PingPongButton'
import { PingPongButtonTypeEnum } from '../utils/ping-pong-button-type.enum.ts'
import { IProposalInteractionsProps } from '../../Proposal'
import {
  ClientProposalOutputDto,
  ClientProposalUserActionEnum,
  ClientRequestOutputDto,
  NonCompliantRulesFragment,
  ServiceProviderFragment,
  UserOutputDto,
} from '../../../../../lib/graphql'
import { Button } from '../../../../../shadcn/components/ui/button'
import { openHubSpotChat } from '../../../../../utils'

interface IUserCurrentActionProps {
  clientProposal: NonCompliantRulesFragment &
    Pick<
      ClientProposalOutputDto,
      | '_id'
      | 'userCurrentAction'
      | 'clientRequestId'
      | 'houseId'
      | 'permissions'
    > & {
      serviceProvider: Maybe<Pick<ServiceProviderFragment, '_id'>>
    }
  userDetails?: Pick<UserOutputDto, 'phoneNumber'>
  userActions: IProposalInteractionsProps['userActions']
  adminActions: IProposalInteractionsProps['adminActions']
  handleDepositPayment?: () => void
  handleBalancePayment?: () => void
  handlePostBalancePayment?: () => void
  clientRequest: Pick<ClientRequestOutputDto, '_id' | 'adminCsm' | 'deposit'>
}

const UserCurrentAction = ({
  clientProposal,
  userDetails,
  userActions,
  adminActions,
  handleDepositPayment,
  handleBalancePayment,
  handlePostBalancePayment,
  clientRequest,
}: IUserCurrentActionProps) => {
  if (!clientProposal.houseId && !clientProposal.serviceProvider?._id) {
    throw new Error('HOUSE_ID_OR_SERVICE_PROVIDER_ID_IS_REQUIRED')
  }

  switch (clientProposal.userCurrentAction) {
    case ClientProposalUserActionEnum.CLIENT_REQUEST_CANCELLED:
      return (
        <div className='w-full py-5'>
          <Button disabled={true} variant='ghost' className='w-full'>
            <FormattedMessage
              id='ui.UserCurrentActionButton.clientRequestCancelled'
              defaultMessage='Évènement annulé'
            />
          </Button>
        </div>
      )
    case ClientProposalUserActionEnum.CAN_ASK_QUOTATION:
      return (
        <UserCanAskQuotationFooter
          clientProposalId={clientProposal._id}
          userActions={userActions}
          phoneNumber={userDetails?.phoneNumber || ''}
        />
      )
    case ClientProposalUserActionEnum.WAITING_HOST_QUOTATION:
      return (
        <PingPongButton
          dataTestId='waiting-host-quotation-button'
          label={
            <FormattedMessage
              id='ui.UserCurrentActionButton.waitingHostQuotation'
              defaultMessage='En attente du devis'
            />
          }
          type={PingPongButtonTypeEnum.WAITING}
        />
      )
    case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_HOST:
      return (
        <PingPongButton
          label={
            <FormattedMessage
              id='ui.UserCurrentActionButton.bookingDeclinedByHost'
              defaultMessage='Lieu non disponible : contacter mon conseiller'
            />
          }
          onClick={openHubSpotChat}
          type={PingPongButtonTypeEnum.HOUSE_NOT_AVAILABLE}
        />
      )
    case ClientProposalUserActionEnum.CAN_ASK_USER_ADMIN_APPROVAL:
      return (
        <UserCanAskUserAdminApprovalFooter
          clientProposal={clientProposal}
          userActions={userActions}
        />
      )
    case ClientProposalUserActionEnum.WAITING_USER_ADMIN_CONFIRMATION:
      return (
        <div className='w-full py-5'>
          <PingPongButton
            label={
              <FormattedMessage
                id='ui.UserCurrentActionButton.waitingUserAdminConfirmation'
                defaultMessage='Attente de réponse de l’administrateur'
              />
            }
            type={PingPongButtonTypeEnum.WAITING}
          />
        </div>
      )
    case ClientProposalUserActionEnum.BOOKING_DECLINED_BY_USER_ADMIN:
      return <BookingDeclinedByUserAdminFooter />
    case ClientProposalUserActionEnum.CAN_CONFIRM_BOOKING:
      return (
        <UserCanConfirmBookingFooter
          clientProposal={clientProposal}
          userActions={userActions}
        />
      )
    case ClientProposalUserActionEnum.WAITING_HOST_BOOKING_CONFIRMATION:
      return (
        <UserWaitingHostBookingConfirmationFooter
          adminActions={adminActions}
          clientProposal={clientProposal}
        />
      )
    case ClientProposalUserActionEnum.CAN_PAY_DEPOSIT:
      return (
        <UserCanPayDepositFooter
          adminActions={adminActions}
          handleDepositPayment={handleDepositPayment}
          clientProposal={clientProposal}
          clientRequest={clientRequest}
        />
      )
    case ClientProposalUserActionEnum.DEPOSIT_PAID:
      return (
        <DepositPaidFooter
          adminActions={adminActions}
          clientProposal={clientProposal}
        />
      )
    case ClientProposalUserActionEnum.CAN_CONFIRM_ADJUSTMENTS:
      return (
        <UserCanConfirmAdjustmentsFooter
          userActions={userActions}
          clientProposal={clientProposal}
        />
      )
    case ClientProposalUserActionEnum.WAITING_HOST_ADJUSTMENTS_CONFIRMATION:
      return (
        <UserWaitingHostAdjustmentsConfirmationFooter
          adminActions={adminActions}
          clientProposalId={clientProposal._id}
        />
      )
    case ClientProposalUserActionEnum.CAN_PAY_BALANCE:
      return (
        <UserCanPayBalanceFooter
          adminActions={adminActions}
          handleBalancePayment={handleBalancePayment}
          clientProposal={clientProposal}
          clientRequest={clientRequest}
        />
      )
    case ClientProposalUserActionEnum.BALANCE_PAID:
      return (
        <BalancePaidFooter
          clientProposalId={clientProposal._id}
          adminActions={adminActions}
        />
      )

    case ClientProposalUserActionEnum.CAN_PAY_POST_BALANCE:
      return (
        <UserCanPayPostBalanceFooter
          adminActions={adminActions}
          handlePostBalancePayment={handlePostBalancePayment}
          clientProposal={clientProposal}
          clientRequest={clientRequest}
        />
      )
    case ClientProposalUserActionEnum.POST_BALANCE_PAID:
      return (
        <PostBalancePaidFooter
          adminActions={adminActions}
          clientProposal={clientProposal}
        />
      )
  }
  return null
}

export default UserCurrentAction
